import { observer } from 'mobx-react'
import React, { ReactElement, useEffect, useMemo, useState } from 'react'
import Spinner from '../components/Spinner'
import { AssociatedEntityType, ManagedPaymentEvent, parseManagedEvent } from '../state/Events'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { RADOM_COLORS } from '../util/Constants'
import styled from 'styled-components'
import TimeText from '../components/TimeText'
import BreadCrumbs from '../components/Breadcrumbs'
import { Container } from '../components/Animations'
import PageHeader from '../components/PageHeader'
import Radom, { Customer, DonationLinkOrder, ICheckoutSession, IConversionRate, Invoice, IPaymentLink, ManagedBalanceLedger, ManagedEventWithRelatedCustomer, PaymentLinkOrder, PaymentSession, PaymentSummary, RelatedCustomer } from '../state/Radom'
import Table from '../components/Table'
import LinkExternal from '../components/LinkExternal'
import { ExternalLinkText } from './Events'
import CheckoutSessionDetails from '../components/CheckoutSessionDetails'
import PaymentSessionDetails from '../components/PaymentSessionDetails'
import { PaymentDetailsSection } from '../components/PaymentDetails'
import Dropdown, { DropdownItem } from '../components/Dropdown'
import Dots from '../icons/Dots'
import { PrimaryButton } from '../components/Button'
import Modal from '../components/Modal'
import { errorToast, roundAssetAmount, trimZeros } from '../util/Util'
import RefundForm from '../components/RefundForm'
import RefundDetails from '../components/RefundDetails'
import _ from 'lodash'
import { getMethod } from '../util/Managed'
import { Chevron } from '../icons/Chevron'
import { Section } from '../components/Shared'
import {
  DetailGrid,
  InlinePill,
  PageActionRow,
  PageHeaderRow,
  PageHeaderStack,
  Surface,
  SurfaceHeader,
  SurfaceSubtext,
  SurfaceTitle,
  SummaryLabel,
  SummaryMeta,
  SummaryStrip,
  SummaryStripItem,
  SummaryValue
} from '../components/DashboardPrimitives'
import Gate from '../components/Gate'
import { usePermissions } from '../util/permissions'

const SubTitle = styled.div`
  font-size: 12.25px;
  font-weight: 500;
  line-height: 1.4;
  color: #6f7888;
`

const Payment = observer((): ReactElement => {
  const params = useParams()
  const [searchParams] = useSearchParams()
  const source = searchParams.get('source')
  const [paymentEvent, setPaymentEvent] = useState<ManagedPaymentEvent>()
  const [isLoading, setIsLoading] = useState(false)
  const [paymentLinkOrder, setPaymentLinkOrder] = useState<PaymentLinkOrder>()
  const [paymentLink, setPaymentLink] = useState<IPaymentLink>()
  const [donationLinkOrder, setDonationLinkOrder] = useState<DonationLinkOrder>()
  const [checkoutSession, setCheckoutSession] = useState<ICheckoutSession>()
  const [paymentSession, setPaymentSession] = useState<PaymentSession>()
  const [invoice, setInvoice] = useState<Invoice>()
  const [isIncomplete, setIsIncomplete] = useState(false)
  const [isIncompleteModalOpen, setIsIncompleteModalOpen] = useState(false)
  const [isCompletingPayment, setIsCompletingPayment] = useState(false)
  const [refundModalOpen, setRefundModalOpen] = useState(false)
  const [customer, setCustomer] = useState<Customer | RelatedCustomer>()
  const [generatationPDFloading, setGeneratationPDFloading] = useState(false)
  const [managedBalanceLedger, setManagedBalanceLedger] = useState<ManagedBalanceLedger>()
  const { has } = usePermissions()
  const [paymentSummary, setPaymentSummary] = useState<{
    in: { amount: string | number, ticker: string, logo: string }
    out?: { amount: string | number, ticker: string, logo: string }
  }>({ in: { amount: '0', ticker: 'USDC', logo: '' } })

  const loadCustomerFromPaymentId = async (paymentId?: string): Promise<void> => {
    if (!paymentId) return

    try {
      const payment = await Radom.getPayment(paymentId)
      if (!payment.customerId) return

      const customer = await Radom.getCustomer(payment.customerId)
      setCustomer(customer)
    } catch {
      // Customer context is helpful, but the payment detail page should still load without it.
    }
  }

  const fetchPaymentData = async (id: string): Promise<void> => {
    setIsLoading(true)
    let paymentEvent: ManagedPaymentEvent
    let isFiatPayment: boolean
    let paymentSummary: PaymentSummary | null = null
    let conversionRate: IConversionRate | null = null
    try {
      const payment = await Radom.getEvent(id)
      setCustomer((payment as ManagedEventWithRelatedCustomer).relatedCustomer)
      paymentEvent = parseManagedEvent(payment) as ManagedPaymentEvent
      paymentSummary = payment.eventData?.payment?.paymentSummary ??
          payment.eventData?.openBanking?.paymentSummary ??
          payment.eventData?.recurringPayment?.paymentSummary ??
          payment.eventData?.subscriptionPayment?.paymentSummary ??
          payment.eventData?.incompletePayment?.paymentSummary ??
          null
      isFiatPayment = !paymentSummary
      const paymentSummaryCurrency =
          payment.eventData?.payment?.paymentSummary?.currency ??
          payment.eventData?.openBanking?.paymentSummary?.currency ??
          payment.eventData?.recurringPayment?.paymentSummary?.currency ??
          payment.eventData?.subscriptionPayment?.paymentSummary?.currency ??
          payment.eventData?.incompletePayment?.paymentSummary?.currency ?? 'USD'
      const conversionRates = payment.eventData?.payment?.conversionRates ??
          payment.eventData?.openBanking?.conversionRates ??
          payment.eventData?.recurringPayment?.conversionRates ??
          payment.eventData?.subscriptionPayment?.conversionRates ??
          payment.eventData?.incompletePayment?.conversionRates ?? []
      conversionRate = conversionRates.find(item =>
        item.from === paymentSummaryCurrency &&
        item.to === paymentEvent.paymentMethod.ticker) ?? null
      const cryptoAmount = paymentSummary?.grossAmount && conversionRate?.rate
        ? Number(paymentSummary.grossAmount) * Number(conversionRate.rate)
        : null
      setPaymentSummary({
        in: {
          amount: cryptoAmount ??
        paymentEvent.amount,
          ticker: paymentEvent.paymentMethod.ticker,
          logo: paymentEvent.paymentMethod.logo ?? ''
        }
      })
      setPaymentEvent(paymentEvent)
      setIsIncomplete(paymentEvent.name === 'IncompletePaymentEvent')
    } catch {
      setIsLoading(false)
      errorToast('Failed to load payment')
      return
    }

    try {
      setPaymentLinkOrder(undefined)
      setCheckoutSession(undefined)
      setPaymentSession(undefined)
      setDonationLinkOrder(undefined)
      setInvoice(undefined)
      setPaymentLink(undefined)

      if (paymentEvent.associatedEntity.entityType === AssociatedEntityType.PaymentLink) {
        const paymentLinkOrder = await Radom.getPaymentLinkOrder(paymentEvent.associatedEntity.id)
        const paymentLink = await Radom.getPaymentLink(paymentLinkOrder.paymentLinkId)
        setPaymentLinkOrder(paymentLinkOrder)
        setPaymentLink(paymentLink)
        await loadCustomerFromPaymentId(
          paymentLinkOrder.payment.managed?.id ??
          paymentLinkOrder.payment.managedTokenDelegatePayment?.id
        )
      }

      if (paymentEvent.associatedEntity.entityType === AssociatedEntityType.PaymentSession) {
        const paymentSession = await Radom.getPaymentSession(paymentEvent.associatedEntity.id)
        setPaymentSession(paymentSession)
        await loadCustomerFromPaymentId(
          paymentSession.payment?.managed?.id ??
          paymentSession.payment?.managedTokenDelegatePayment?.id
        )
      }

      if (paymentEvent.associatedEntity.entityType === AssociatedEntityType.CheckoutSession) {
        const checkoutSession = await Radom.getCheckoutSession(paymentEvent.associatedEntity.id)
        const cryptoAmount = paymentSummary?.grossAmount && conversionRate?.rate
          ? Number(paymentSummary.grossAmount) * Number(conversionRate.rate)
          : null
        if (
          checkoutSession.payment?.openBanking &&
          checkoutSession.payment?.openBanking.onrampGrossAmount &&
          checkoutSession.payment?.openBanking.onrampNetwork &&
          checkoutSession.payment?.openBanking.onrampToken &&
          isFiatPayment
        ) {
          const openBankingMethod = getMethod(
            checkoutSession.payment?.openBanking.onrampNetwork,
            checkoutSession.payment?.openBanking.onrampToken
          )
          setPaymentSummary({
            in: {
              amount: cryptoAmount ??
        paymentEvent.amount,
              ticker: paymentEvent.paymentMethod.ticker,
              logo: paymentEvent.paymentMethod.logo ?? ''
            },
            out: {
              amount: checkoutSession.payment?.openBanking.onrampGrossAmount,
              ticker: openBankingMethod.ticker,
              logo: openBankingMethod.logo ?? ''
            }
          })
        }
        setCheckoutSession(checkoutSession)
        await loadCustomerFromPaymentId(
          checkoutSession.payment?.managed?.id ??
          checkoutSession.payment?.managedTokenDelegatePayment?.id ??
          checkoutSession.payment?.openBanking?.id
        )
      }

      if (paymentEvent.associatedEntity.entityType === AssociatedEntityType.Invoice) {
        const invoice = await Radom.getInvoice(paymentEvent.associatedEntity.id)
        setInvoice(invoice)
        setCustomer(invoice.customer)
      }

      if (paymentEvent.associatedEntity.entityType === AssociatedEntityType.DonationLink) {
        const donationLinkOrder = await Radom.getDonationLinkOrder(paymentEvent.associatedEntity.id)
        const paymentId = donationLinkOrder.payment.managed?.id
        if (paymentId) {
          const payment = await Radom.getPayment(paymentId)
          if (payment.customerId) {
            const customer = await Radom.getCustomer(payment.customerId)
            setCustomer(customer)
          }
        }
        setDonationLinkOrder(donationLinkOrder)
      }
    } catch {
      errorToast('Failed to load payment details')
    }

    try {
      const ledger = await Radom.listManagedBalanceLedger([paymentEvent.associatedEntity.id])
      if (ledger.length > 0) { setManagedBalanceLedger(ledger[0]) }
    } catch {
      errorToast('Failed to load ledger')
    }
    setIsLoading(false)
  }

  const debouncedFetchPaymentData = useMemo(() => _.debounce(fetchPaymentData, 300), [])

  const generateReceipt = async (): Promise<void> => {
    try {
      if (!params.id) return
      setGeneratationPDFloading(true)
      const data = await Radom.generateReceipt(params.id)
      if (data.url !== '') {
        window.open(data.url, '_blank')
      }
    } catch (err) {
      errorToast(err.reason || err.message || err.toString())
    } finally {
      setGeneratationPDFloading(false)
    }
  }

  useEffect(() => {
    if (!params.id) return
    debouncedFetchPaymentData(params.id)
    return () => debouncedFetchPaymentData.cancel()
  }, [params.id])

  if (isLoading) {
    return <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 140, gap: 20 }}>
      <Spinner />
    </div>
  }

  if (paymentEvent) {
    const renderAssociatedEntity = (): React.ReactNode => {
      if (
        paymentEvent.associatedEntity &&
        paymentEvent.associatedEntity.entityType !== AssociatedEntityType.PaymentSession &&
        paymentEvent.associatedEntity.entityType !== AssociatedEntityType.PaymentLink
      ) {
        return <ExternalLinkText
          onClick={e => e.stopPropagation()}
          href={paymentEvent.associatedEntity.link}
          target="_blank"
          rel="noreferrer">
          <span>{AssociatedEntityType[paymentEvent.associatedEntity.entityType]}</span>
          <span>{paymentEvent.associatedEntity.id}</span>
          <LinkExternal stroke={RADOM_COLORS.BLACK} width={13} />
        </ExternalLinkText>
      }

      if (
        paymentEvent.associatedEntity &&
        paymentEvent.associatedEntity.entityType === AssociatedEntityType.PaymentLink
      ) {
        return paymentLink
          ? <ExternalLinkText
            onClick={e => e.stopPropagation()}
            href={paymentLink.isPresale ? `/presales/${paymentLink.id}` : `/payment_link/detail/${paymentLink.id}`}
            target="_blank"
            rel="noreferrer">
            <span>{paymentLink.isPresale ? 'Presale link' : 'Payment link'}</span>
            <span>{paymentLink.id}</span>
            <LinkExternal stroke={RADOM_COLORS.BLACK} width={13} />
          </ExternalLinkText>
          : '-'
      }

      if (
        paymentEvent.associatedEntity &&
        paymentEvent.associatedEntity.entityType === AssociatedEntityType.PaymentSession
      ) {
        return <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
          <span>{AssociatedEntityType[paymentEvent.associatedEntity.entityType]}</span>
          <span>{paymentEvent.associatedEntity.id}</span>
        </div>
      }

      return 'No associated entity'
    }

    const paymentActions = <>
      {
        isIncomplete &&
        <Dropdown
          noChevron
          iconTrigger
          selectedContent={<Dots width={15} />}
          dropdownContent={
            <div style={{ fontSize: 14, whiteSpace: 'nowrap' }}>
              <DropdownItem onClick={() => setIsIncompleteModalOpen(true)}>Complete payment</DropdownItem>
            </div>
          }
        />
      }

      {
        !isIncomplete && (
          ((paymentSession || donationLinkOrder || paymentLinkOrder || checkoutSession) && paymentEvent.name !== 'RecurringPaymentEvent') && (
            <Dropdown
              noChevron
              iconTrigger
              selectedContent={<Dots width={15} />}
              dropdownContent={
                <div style={{ fontSize: 14, whiteSpace: 'nowrap' }}>
                  {(paymentSession || donationLinkOrder) && has('WriteRefunds') && (
                    <DropdownItem onClick={() => setRefundModalOpen(true)}>
                      Refund payment
                    </DropdownItem>
                  )}
                  {(paymentLinkOrder || checkoutSession) && (
                    <DropdownItem onClick={generateReceipt}>
                      {generatationPDFloading ? <Spinner /> : 'Export PDF'}
                    </DropdownItem>
                  )}
                  {
                    checkoutSession?.payment && has('WriteRefunds') &&
                    <DropdownItem onClick={() => setRefundModalOpen(true)}>Refund order</DropdownItem>
                  }
                </div>
              }
            />
          )
        )
      }
    </>

    return <Container style={{ flexDirection: 'column', fontSize: 14, gap: 30 }}>

      <PageHeaderRow>
        <PageHeaderStack>
          <BreadCrumbs items={
            source === 'transactions'
              ? [
                { item: 'Transactions', to: '/transactions' },
                { item: paymentEvent.id, to: `/payment/${paymentEvent.id}?source=transactions` }
              ]
              : [
                { item: 'Transactions', to: '/transactions' },
                { item: paymentEvent.id, to: '.' }
              ]} />
          <PageHeader title='Payment' subTitle={paymentEvent.id} />
        </PageHeaderStack>
        <PageActionRow>
          {paymentActions}
        </PageActionRow>
      </PageHeaderRow>

      {
        refundModalOpen &&
        <Modal visible={refundModalOpen} onClose={() => setRefundModalOpen(false)} title='Refund payment'>
          <div style={{ width: 500, padding: '0 30px 30px 30px' }}>
            <RefundForm
              paymentSession={paymentSession}
              donationLinkOrder={donationLinkOrder}
              checkoutSession={checkoutSession}
              onRefund={() => setRefundModalOpen(false)} />
          </div>
        </Modal>
      }

      {managedBalanceLedger
        ? <DetailGrid>
          <Surface style={{ gap: 16 }}>
            <SurfaceHeader>
              <div>
                <SurfaceTitle>Payment overview</SurfaceTitle>
                <SurfaceSubtext>{paymentEvent.id}</SurfaceSubtext>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <InlinePill $tone='neutral'>{paymentEvent.label}</InlinePill>
                {isIncomplete && <InlinePill $tone='amber'>Incomplete</InlinePill>}
              </div>
            </SurfaceHeader>

            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <img src={paymentSummary.in.logo} style={{ width: 32, height: 'auto', maxHeight: 32 }} />
                <span style={{ fontSize: 34, lineHeight: 1, fontWeight: 500, color: RADOM_COLORS.BLACK }}>
                  {trimZeros(paymentSummary.in.amount.toString())}
                </span>
                <span style={{
                  background: '#f3f5f9',
                  color: RADOM_COLORS.BLACK,
                  borderRadius: 999,
                  fontSize: 13,
                  padding: '6px 11px',
                  fontWeight: 500
                }}>{paymentSummary.in.ticker}</span>
              </div>

              {paymentSummary.out && <>
                <Chevron fill={RADOM_COLORS.GRAY_DARKER} style={{ width: 16, height: 16, transform: 'rotate(-90deg)' }} />
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <img src={paymentSummary.out.logo } style={{ width: 32, height: 'auto', maxHeight: 32 }} />
                  <span style={{ fontSize: 34, lineHeight: 1, fontWeight: 500, color: RADOM_COLORS.BLACK }}>
                    {trimZeros(paymentSummary.out.amount.toString())}
                  </span>
                  <span style={{
                    background: '#f3f5f9',
                    color: RADOM_COLORS.BLACK,
                    borderRadius: 999,
                    fontSize: 13,
                    padding: '6px 11px',
                    fontWeight: 500
                  }}>{paymentSummary.out.ticker}</span>
                </div>
              </>}
            </div>

            <SummaryStrip style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
              <SummaryStripItem>
                <SummaryLabel>Type</SummaryLabel>
                <SummaryValue>{paymentEvent.label}</SummaryValue>
                <SummaryMeta>Managed payment event</SummaryMeta>
              </SummaryStripItem>
              <SummaryStripItem>
                <SummaryLabel>Associated entity</SummaryLabel>
                <div style={{
                  minWidth: 0,
                  fontSize: 14.5,
                  fontWeight: 500,
                  lineHeight: 1.35,
                  color: RADOM_COLORS.BLACK
                }}>
                  {renderAssociatedEntity()}
                </div>
              </SummaryStripItem>
              <SummaryStripItem>
                <SummaryLabel>Transaction date</SummaryLabel>
                <div style={{
                  minWidth: 0,
                  fontSize: 14.5,
                  fontWeight: 500,
                  lineHeight: 1.35,
                  color: RADOM_COLORS.BLACK
                }}>
                  <TimeText textColor={RADOM_COLORS.BLACK} date={paymentEvent.date} />
                </div>
              </SummaryStripItem>
              <SummaryStripItem>
                <SummaryLabel>Customer</SummaryLabel>
                {customer
                  ? <Link to={`/customer/${customer.id}`} style={{
                    fontSize: 14.5,
                    fontWeight: 500,
                    color: RADOM_COLORS.BLUE_MED,
                    textDecoration: 'none',
                    lineHeight: 1.35
                  }}>
                    {customer.name || customer.email}
                  </Link>
                  : <SummaryValue>Not attached</SummaryValue>}
                <SummaryMeta>{customer ? 'View customer profile' : 'No customer linked to this payment'}</SummaryMeta>
              </SummaryStripItem>
            </SummaryStrip>
          </Surface>

          <Surface style={{ gap: 14 }}>
            <div>
              <SurfaceTitle>Ledger</SurfaceTitle>
              <SurfaceSubtext>
                {paymentSummary.out?.ticker ?? paymentSummary.in.ticker} on {managedBalanceLedger.network}
              </SurfaceSubtext>
            </div>
            <SummaryStrip style={{ gridTemplateColumns: '1fr' }}>
              <SummaryStripItem>
                <SummaryLabel>Balance before</SummaryLabel>
                <SummaryValue>
                  {roundAssetAmount(
                    paymentSummary.out?.ticker ?? paymentSummary.in.ticker,
                    Number(managedBalanceLedger.balanceBefore)
                  )}
                </SummaryValue>
              </SummaryStripItem>
              <SummaryStripItem>
                <SummaryLabel>Balance after</SummaryLabel>
                <SummaryValue>
                  {roundAssetAmount(
                    paymentSummary.out?.ticker ?? paymentSummary.in.ticker,
                    Number(managedBalanceLedger.balanceAfter)
                  )}
                </SummaryValue>
              </SummaryStripItem>
            </SummaryStrip>
          </Surface>
        </DetailGrid>
        : <Surface style={{ gap: 16 }}>
          <SurfaceHeader>
            <div>
              <SurfaceTitle>Payment overview</SurfaceTitle>
              <SurfaceSubtext>{paymentEvent.id}</SurfaceSubtext>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <InlinePill $tone='neutral'>{paymentEvent.label}</InlinePill>
              {isIncomplete && <InlinePill $tone='amber'>Incomplete</InlinePill>}
            </div>
          </SurfaceHeader>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <img src={paymentSummary.in.logo} style={{ width: 32, height: 'auto', maxHeight: 32 }} />
              <span style={{ fontSize: 34, lineHeight: 1, fontWeight: 500, color: RADOM_COLORS.BLACK }}>
                {trimZeros(paymentSummary.in.amount.toString())}
              </span>
              <span style={{
                background: '#f3f5f9',
                color: RADOM_COLORS.BLACK,
                borderRadius: 999,
                fontSize: 13,
                padding: '6px 11px',
                fontWeight: 500
              }}>{paymentSummary.in.ticker}</span>
            </div>

            {paymentSummary.out && <>
              <Chevron fill={RADOM_COLORS.GRAY_DARKER} style={{ width: 16, height: 16, transform: 'rotate(-90deg)' }} />
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <img src={paymentSummary.out.logo } style={{ width: 32, height: 'auto', maxHeight: 32 }} />
                <span style={{ fontSize: 34, lineHeight: 1, fontWeight: 500, color: RADOM_COLORS.BLACK }}>
                  {trimZeros(paymentSummary.out.amount.toString())}
                </span>
                <span style={{
                  background: '#f3f5f9',
                  color: RADOM_COLORS.BLACK,
                  borderRadius: 999,
                  fontSize: 13,
                  padding: '6px 11px',
                  fontWeight: 500
                }}>{paymentSummary.out.ticker}</span>
              </div>
            </>}
          </div>

          <SummaryStrip style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
            <SummaryStripItem>
              <SummaryLabel>Type</SummaryLabel>
              <SummaryValue>{paymentEvent.label}</SummaryValue>
              <SummaryMeta>Managed payment event</SummaryMeta>
            </SummaryStripItem>
            <SummaryStripItem>
              <SummaryLabel>Associated entity</SummaryLabel>
              <div style={{
                minWidth: 0,
                fontSize: 14.5,
                fontWeight: 500,
                lineHeight: 1.35,
                color: RADOM_COLORS.BLACK
              }}>
                {renderAssociatedEntity()}
              </div>
            </SummaryStripItem>
            <SummaryStripItem>
              <SummaryLabel>Transaction date</SummaryLabel>
              <div style={{
                minWidth: 0,
                fontSize: 14.5,
                fontWeight: 500,
                lineHeight: 1.35,
                color: RADOM_COLORS.BLACK
              }}>
                <TimeText textColor={RADOM_COLORS.BLACK} date={paymentEvent.date} />
              </div>
            </SummaryStripItem>
            <SummaryStripItem>
              <SummaryLabel>Customer</SummaryLabel>
              {customer
                ? <Link to={`/customer/${customer.id}`} style={{ fontSize: 14.5, fontWeight: 500, color: RADOM_COLORS.BLUE_MED, textDecoration: 'none', lineHeight: 1.35 }}>
                  {customer.name || customer.email}
                </Link>
                : <SummaryValue>Not attached</SummaryValue>}
              <SummaryMeta>{customer ? 'View customer profile' : 'No customer linked to this payment'}</SummaryMeta>
            </SummaryStripItem>
          </SummaryStrip>
        </Surface>}

      {
        paymentLinkOrder && paymentLinkOrder.orderData.length > 0 &&
        <Section>
          <SubTitle style={{
            fontSize: 18,
            paddingBottom: 5
          }}>Order details</SubTitle>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 30 }}>

            <div style={{ fontSize: 14 }}>
              <SubTitle style={{ fontSize: 14, paddingBottom: 10 }}>Customer input</SubTitle>
              <Table
                headers={[
                  <span key="productType">Input type</span>,
                  <span key="quantity">Value</span>
                ]}
                items={
                  paymentLinkOrder.orderData.map(p => [
                    <span key="type">{p.key}</span>,
                    <span key="quantity">{p.value}</span>
                  ])
                }
                isLoading={false}
              />
            </div>

          </div>
        </Section>
      }

      {
        donationLinkOrder && donationLinkOrder.orderData.length > 0 &&
        <Section>
          <SubTitle style={{
            fontSize: 18,
            paddingBottom: 5
          }}>Donation details</SubTitle>

          <div style={{ fontSize: 14 }}>
            <SubTitle style={{ fontSize: 14, paddingBottom: 10 }}>Customer input</SubTitle>
            <Table
              headers={[
                <span key="productType">Input type</span>,
                <span key="quantity">Value</span>
              ]}
              items={
                donationLinkOrder.orderData.map(p => {
                  const valueElement = p.value.toString()

                  return [
                    <span key={`type-${p.key}`}>{p.key}</span>,
                    <span key={`quantity-${p.key}`}>{valueElement}</span>
                  ]
                })
              }
              isLoading={false}
            />
          </div>
        </Section>
      }

      {
        checkoutSession &&
        <CheckoutSessionDetails checkoutSession={checkoutSession} netPayment={paymentEvent.amount} />
      }

      {
        paymentSession &&
        <PaymentSessionDetails paymentSession={paymentSession} netPayment={paymentEvent.amount} />
      }

      {
        invoice?.payment?.managed &&
        <PaymentDetailsSection payment={invoice.payment} netPayment={paymentEvent.amount} />
      }

      {
        paymentLinkOrder?.payment?.managed &&
        <PaymentDetailsSection payment={paymentLinkOrder.payment} netPayment={paymentEvent.amount} />
      }

      {
        donationLinkOrder?.payment?.managed &&
        <PaymentDetailsSection payment={donationLinkOrder.payment} netPayment={paymentEvent.amount} />
      }

      {
        paymentLinkOrder?.payment?.managedTokenDelegatePayment &&
        <PaymentDetailsSection
          payment={paymentLinkOrder.payment}
          netPayment={paymentEvent.amount} />
      }

      {
        donationLinkOrder && donationLinkOrder.refunds.length > 0 &&
        <RefundDetails refunds={donationLinkOrder.refunds} />
      }

      {
        isIncompleteModalOpen &&
        <Modal
          title='Complete payment'
          visible={isIncompleteModalOpen}
          onClose={() => setIsIncompleteModalOpen(false)}
          innerStyles={{ width: '400px', minHeight: 'auto' }}>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between'
          }}>
            <div style={{ padding: '20px 30px' }}>
              <span style={{ fontSize: 14, color: RADOM_COLORS.GRAY_DARKEST, whiteSpace: 'pre-wrap' }}>
                Are you sure you would like to mark this payment as complete?
                This will trigger any post-payment actions and is irreversible.
              </span>
            </div>
            <Gate permission="WriteIncompletePayments">
              <div style={{ padding: 30 }}>
                <PrimaryButton
                  isLoading={isCompletingPayment}
                  style={{ width: '100%' }}
                  onClick={async () => {
                    setIsCompletingPayment(true)
                    try {
                      await Radom.completePayment({ eventId: params.id })
                      window.location.reload()
                    } catch (err) {
                      errorToast(err.reason || err.message || err.toString())
                    }
                    setIsCompletingPayment(false)
                  }}>
                Complete payment
                </PrimaryButton>
              </div>
            </Gate>
          </div>
        </Modal>
      }
    </Container>
  }

  return <div>
    <span>Order not found</span>
  </div>
})

export default Payment
